import React, { useState, useEffect, useRef } from 'react';
import Icon from '@mdi/react';
import { mdiChevronDown, mdiChevronRight, mdiInformationOutline } from '@mdi/js';
import './ClassesHelpModal.css';

/**
 * Page-scoped help for the Finances dashboard. Broad finances topics (Stripe setup, payout
 * country, platform fee, payouts, refunds overview) live on the Manage Finances portal help
 * modal; this one covers only reading the dashboard and the transactions table.
 */
const FinancesDashboardHelpModal = ({ isOpen, onClose }) => {
    const [expandedSections, setExpandedSections] = useState([]);
    const sectionRefs = useRef({});

    useEffect(() => {
        if (!isOpen) setExpandedSections([]);
    }, [isOpen]);

    const toggleSection = (sectionId) => {
        const isExpanding = !expandedSections.includes(sectionId);
        setExpandedSections(prev =>
            prev.includes(sectionId)
                ? prev.filter(id => id !== sectionId)
                : [...prev, sectionId]
        );
        if (isExpanding) {
            setTimeout(() => {
                sectionRefs.current[sectionId]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 0);
        }
    };

    if (!isOpen) return null;

    const sections = [
        {
            id: 'overview',
            title: '1. Reading the Finances dashboard',
            content: (
                <>
                    <p>This page gives you an overview of all payment activity across classes, private lessons, and events. From here you can:</p>
                    <ul>
                        <li>View a revenue summary by period</li>
                        <li>See individual transaction history across all payment types</li>
                        <li>Track payout history and upcoming scheduled transfers to your bank</li>
                        <li>Monitor outstanding payments and unpaid enrolments</li>
                    </ul>
                </>
            )
        },
        {
            id: 'transactions-table',
            title: '2. The transactions table',
            content: (
                <>
                    <p>Each row is one payment. It shows the <strong>type</strong> (class, private lesson, ticket, adjustment, refund, or credit), who paid, the amount, the date, and a status.</p>
                    <ul>
                        <li>Payments taken online through Stripe show a paid status.</li>
                        <li>Payments taken offline (cash, EFTPOS, bank transfer) are badged <strong>"paid outside the platform"</strong>, and can be ticked off on the Reconcile Payments page.</li>
                        <li>An <strong>Adjustment</strong> is a flat charge you added yourself (via <strong>Add charge</strong> on a student's Payments tab) that isn't tied to a class package, for example a drop-in/walk-in fee, school merchandise, or a balance correction.</li>
                        <li>Refunds appear with a Refund badge and a negative amount.</li>
                        <li><strong>Abandoned</strong> (grey) means a customer opened a checkout but never paid, so it was closed automatically after a day. No money moved, and these rows are not counted in your totals or the transaction count. Pick <strong>Abandoned</strong> in the Status filter to see only these.</li>
                    </ul>
                    <p>Use <strong>Export CSV</strong> to download the filtered list for your own records.</p>
                </>
            )
        },
        {
            id: 'more-help',
            title: '3. Setup, payouts, fees and refunds',
            content: (
                <>
                    <p>For connecting your Stripe account, choosing your payout country, the platform fee, payout schedules, and how to issue refunds, open the help on the <strong>Manage Finances</strong> page (the page this one sits under).</p>
                    <p>For reconciling external (offline) payments against your bank statement, see the help on the <strong>Reconcile Payments</strong> page.</p>
                </>
            )
        }
    ];

    return (
        <div className="classes-help-overlay" onClick={onClose}>
            <div className="classes-help-modal" onClick={e => e.stopPropagation()}>
                <div className="classes-help-header">
                    <div className="classes-help-header-content">
                        <Icon path={mdiInformationOutline} size={1.2} color="var(--dl-color-purple-default)" />
                        <h2>Finances Help</h2>
                    </div>
                    <button className="classes-help-close" onClick={onClose}>×</button>
                </div>

                <div className="classes-help-body">
                    {sections.map(section => (
                        <div key={section.id} className="help-accordion-section" ref={el => sectionRefs.current[section.id] = el}>
                            <button
                                className="help-accordion-header"
                                onClick={() => toggleSection(section.id)}
                            >
                                <Icon
                                    path={expandedSections.includes(section.id) ? mdiChevronDown : mdiChevronRight}
                                    size={0.8}
                                    color="var(--dl-color-purple-default)"
                                />
                                <span>{section.title}</span>
                            </button>
                            {expandedSections.includes(section.id) && (
                                <div className="help-accordion-content">
                                    {section.content}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                <div className="classes-help-footer">
                    <button className="classes-help-btn-secondary" onClick={onClose}>Got it!</button>
                </div>
            </div>
        </div>
    );
};

export default FinancesDashboardHelpModal;
